import Reflux from 'reflux';
import BaseMixin from 'flux/stores/BaseMixin';
import { Map, OrderedMap, fromJS } from 'immutable';
import GlobalElementModalActions from 'flux/actions/GlobalElementModalActions';
import CuesheetActions from 'flux/actions/cuesheet/CuesheetActions';
import CuesheetRowActions from 'flux/actions/cuesheet/CuesheetRowActions';
import CuesheetCellActions from 'flux/actions/cuesheet/CuesheetCellActions';
import NewGlobalElementModalActions from 'flux/actions/NewGlobalElementModalActions';
import RealTimeActions from 'flux/actions/RealTimeActions';
import draftJsUtils from '@shoflo-rundown/draft-js-utils';
import { isGlobalElementManager } from 'main/components/show/cuesheet/cuesheetViewHelper';
import { calculateDerivedFieldsForRows } from '@shoflo-rundown/cuesheet-data-helper';
import {
  applyRowMoveDelta,
  remoteRowOrderDeferred,
} from 'flux/stores/globalElementRowOrderSync';

/**
 *  Global Element Store
 */
export default Reflux.createStore({
  listenables: [GlobalElementModalActions],

  mixins: [BaseMixin],

  init() {
    this._init();

    this.listenTo(CuesheetActions.populate, 'onCuesheetPopulate');
    this.listenTo(
      CuesheetActions.populate.completed,
      'onCuesheetPopulateCompleted',
    );

    this.listenTo(
      CuesheetRowActions.deleteItemRowsPersistent,
      'onDeleteItemRowsPersistent',
    );
    this.listenTo(
      CuesheetRowActions.deleteItemRowsPersistent.completed,
      'onDeleteItemRowsPersistentCompleted',
    );
    this.listenTo(
      CuesheetRowActions.addItemRowsPersistent.completed,
      'onAddItemRowsPersistentCompleted',
    );
    this.listenTo(
      CuesheetRowActions.addGlobalElementItemRowsPersistent.completed,
      'onAddGlobalElementItemRowsPersistentCompleted',
    );
    this.listenTo(
      CuesheetRowActions.addGlobalElementItemRowsTransient,
      'onAddGlobalElementItemRowsTransient',
    );
    this.listenTo(
      CuesheetRowActions.moveFromTo,
      'onMoveRowFromCurrentPositionToIndex',
    );
    this.listenTo(CuesheetRowActions.rowsMovedLocally, 'onRowsMovedLocally');
    this.listenTo(CuesheetRowActions.rowOrderResynced, 'onRowOrderResynced');
    this.listenTo(
      CuesheetRowActions.updateItemRowPersistent,
      'onUpdateItemRowPersistent',
    );
    this.listenTo(
      CuesheetRowActions.convertToTitleRowPersistent,
      'onConvertToTitleRowPersistent',
    );
    this.listenTo(
      CuesheetRowActions.convertToStandardRowPersistent,
      'onConvertToStandardRowPersistent',
    );
    this.listenTo(
      CuesheetRowActions.retrieveLatestGlobalElementVersionPersistent.completed,
      'onRetrieveLatestGlobalElementVersionPersistentCompleted',
    );

    this.listenTo(
      CuesheetCellActions.saveMultipleDataCellChangesPersistent,
      'onSaveMultipleDataCellChangesPersistent',
    );
    this.listenTo(CuesheetCellActions.saveItemNumber, 'onSaveItemNumber');

    this.listenTo(
      RealTimeActions.itemRowsAddedRealTime,
      'onItemRowsAddedRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowsMovedRealTime,
      'onItemRowsMovedRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowsDeletedRealTime,
      'onItemRowsDeletedRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowMutatedRealTime,
      'onItemRowMutatedRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowMultipleCellsMutatedRealTime,
      'onItemRowMultipleCellsMutatedRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowOrderUpdatedRealTime,
      'onItemRowOrderUpdatedRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowMovedFromSourceToDestinationRealTime,
      'onItemRowMovedFromSourceToDestinationRealTime',
    );
    this.listenTo(
      RealTimeActions.itemRowPresentationUpdatedRealTime,
      'onItemRowPresentationUpdatedRealTime',
    );
    this.listenTo(
      RealTimeActions.publishGlobalElementChangesRealTime,
      'onPublishGlobalElementChangesRowRealTime',
    );
    this.listenTo(
      RealTimeActions.globalElementItemRowsAddedRealTime,
      'onGlobalElementItemRowsAddedRealTime',
    );
    this.listenTo(
      RealTimeActions.createdGlobalElementFromShowRowRealTime,
      'onCreatedGlobalElementFromShowRowRealTime',
    );
    this.listenTo(
      NewGlobalElementModalActions.createGlobalElementFromShowRow.completed,
      '_refreshGlobalElementRowAssociations',
    );
  },

  onCreatedGlobalElementFromShowRowRealTime(params, payload) {
    const currentShowId = this.getInState('showId');
    if (!currentShowId) return;

    const affectsCurrentShow = (payload?.rows || []).some(
      (row) => row.show === currentShowId,
    );
    if (!affectsCurrentShow) return;

    this._refreshGlobalElementRowAssociations();
  },

  _init() {
    this.setStateAndTrigger(
      Map({
        rows: OrderedMap(),
        events: OrderedMap(),
        latestRenderedCuesheetRowIndex: 0,
      }),
    );
  },

  onRetrieveLatestGlobalElementVersionPersistentCompleted(updatedRow) {
    let next = this.getState();

    next = next.update('itemSet', (itemSet) => {
      const rowIndex = itemSet.rows.findIndex(
        (row) => row._id === updatedRow._id,
      );

      if (itemSet.rows[rowIndex]) {
        itemSet.rows[rowIndex] = updatedRow;
      }

      return itemSet;
    });

    let geLookup = {};
    next
      .get('globalElementShowItems')
      .forEach((item) => (geLookup[item.row] = item));

    let rowData = Map({
      _id: updatedRow._id,
      item: fromJS(updatedRow.item[0]),
      titleRow: updatedRow.titleRow,
      globalElement: geLookup[updatedRow._id],
    });

    next = next.setIn(['rows', updatedRow._id], rowData);

    next = this._recalculateDerivedFields(next);

    this.setStateAndTrigger(next);
  },

  onAddGlobalElementItemRowBelow(
    {
      eventId,
      showId,
      itemSetListId,
      itemSetId,
      globalElementRowId,
      globalElementRow,
    },
    afterRowId,
  ) {
    const state = this.getState();

    CuesheetRowActions.addGlobalElementItemRowBelow(
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      state.get('itemSetId'),
      afterRowId,
      {
        event: eventId,
        show: showId,
        itemSetList: itemSetListId,
        itemSet: itemSetId,
        row: globalElementRowId,
      },
      globalElementRow,
    );
  },

  onReorderShowRows(sourceRowId, destinationIndex) {
    const state = this.getState();

    CuesheetRowActions.moveFromTo(
      sourceRowId,
      destinationIndex,
      Map({
        teamId: state.get('teamId'),
        eventId: state.get('eventId'),
        showId: state.get('showId'),
        itemSetListId: state.get('itemSetListId'),
        itemSetId: state.get('itemSetId'),
      }),
    );
  },

  onDeleteRow(rowId) {
    const state = this.getState();

    const teamId = state.get('teamId'),
      eventId = state.get('eventId'),
      showId = state.get('showId'),
      itemSetListId = state.get('itemSetListId'),
      itemSetId = state.get('itemSetId');

    CuesheetRowActions.deleteItemRowsPersistent(
      teamId,
      eventId,
      showId,
      itemSetListId,
      itemSetId,
      [rowId],
    );

    /**
     * The rowId to deleteItemRowTransient passed may not exist in the cuesheet store thus having not
     * effect on the rows in the cuesheet store. This is because in the insert
     * global element modal the show context can change via a dropdown selector.
     */
    CuesheetRowActions.deleteItemRowTransient(rowId);
  },

  onPopulateEventListCompleted(events) {
    this.setInStateAndTrigger('events', fromJS(this._getEventItems(events)));
  },

  _getEventItems(events) {
    return events && events.length > 0
      ? events.map((event) => {
          event.shows = event.shows.filter((show) => {
            return show.archived || show.deleted || show.saved || show.template
              ? false
              : true;
          });

          return event;
        })
      : [];
  },

  // eslint-disable-next-line no-unused-vars
  onCuesheetPopulate(teamId, eventId, showId) {
    let next = this.getState();
    if (isGlobalElementManager()) return;

    if (next.get('teamId') !== teamId || next.get('eventId') !== eventId) {
      next = next.set('teamId', teamId).set('eventId', eventId);

      this.setStateAndTrigger(next);
    }
  },

  // eslint-disable-next-line no-unused-vars
  onPopulate(teamId, eventId, showId) {
    let next = this.getState();

    if (next.get('teamId') !== teamId || next.get('eventId') !== eventId) {
      next = next.set('teamId', teamId).set('eventId', eventId);

      this.setStateAndTrigger(next);
    }
  },

  onCuesheetPopulateCompleted(result) {
    let next = this.getState();

    if (isGlobalElementManager()) return;

    const { show, itemSetList, globalElementShowItems } = result;
    const itemSet = itemSetList.itemSets[0];

    next = next
      .set('activeShow', fromJS(show))
      .set('showId', show._id)
      .set('itemSetListId', itemSetList._id)
      .set('itemSetId', itemSet._id)
      .set('itemSet', itemSet)
      .set('globalElementShowItems', globalElementShowItems);

    next = this._setPopulatedShowRows(itemSet.rows, itemSet.rowOrder, next);

    this.setStateAndTrigger(next);
  },

  onPopulateCompleted(result) {
    const { show, itemSetList, globalElementShowItems } = result;
    const itemSet = itemSetList.itemSets[0];

    let next = this.getState()
      .set('activeShow', fromJS(show))
      .set('showId', show._id)
      .set('itemSetListId', itemSetList._id)
      .set('itemSetId', itemSet._id)
      .set('itemSet', itemSet)
      .set('globalElementShowItems', globalElementShowItems);

    next = this._setPopulatedShowRows(itemSet.rows, itemSet.rowOrder, next);

    this.setStateAndTrigger(next);
  },

  _refreshGlobalElementRowAssociations() {
    const state = this.getState();

    if (!state.get('teamId') || !state.get('eventId') || !state.get('showId'))
      return;

    GlobalElementModalActions.refreshGlobalElementShowItems(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
    );
  },

  onRefreshGlobalElementShowItemsCompleted(globalElementShowItems) {
    let next = this.getState();

    let geLookup = {};
    globalElementShowItems.forEach((item) => (geLookup[item.row] = item));

    next = next
      .update('rows', (rows) => {
        return rows.map((row, rowId) => {
          return row.set('globalElement', geLookup[rowId]);
        });
      })
      .set('globalElementShowItems', globalElementShowItems);

    this.setStateAndTrigger(next);
  },

  /**
   * Used to populate current show rows. These are the rows for the left
   * side of the insert global element modal
   */
  _setPopulatedShowRows(rows, rowOrder, state) {
    let next = state || this.getState();

    let geLookup = {};
    next
      .get('globalElementShowItems')
      .forEach((item) => (geLookup[item.row] = item));

    let rowLookup = {};
    rows.forEach((row) => (rowLookup[row._id] = row));

    let rowItems = OrderedMap();

    let filteredRowOrder = [];

    //Filter any "corrupt" rows that don't have associated row data
    rowOrder.forEach((rowId) => {
      let row = rowLookup[rowId];
      if (row) {
        filteredRowOrder.push(rowId);
        rowItems = rowItems.set(
          row._id,
          Map({
            _id: row._id,
            item: fromJS(row.item[0]),
            titleRow: row.titleRow,
            globalElement: geLookup[row._id],
          }),
        );
      }
    });

    const derivedFields = calculateDerivedFieldsForRows(
      next.get('activeShow').toJS(),
      filteredRowOrder,
      rowItems,
      (rowId, rowIndex, state) => {
        return state.getIn([rowId, 'item']).toJS();
      },
    );

    rowItems = rowItems.map((row, rowId) => {
      const numberAdjusted = derivedFields[rowId].number;
      return row.setIn(['item', 'numberAdjusted'], numberAdjusted);
    });

    next = next.set('rows', rowItems);

    return next;
  },

  _recalculateDerivedFields(state) {
    let next = state || this.getState();

    const rows = next.get('rows').toJS();

    let filteredRowOrder = [];
    let rowLookup = {};
    Object.values(rows).forEach((row) => {
      rowLookup[row._id] = row;
    });

    const itemSet = next.get('itemSet');

    //Filter any "corrupt" rows that don't have associated row data
    itemSet.rowOrder.forEach((rowId) => {
      let row = rowLookup[rowId];
      if (row) {
        filteredRowOrder.push(rowId);
      }
    });

    const derivedFields = calculateDerivedFieldsForRows(
      next.get('activeShow').toJS(),
      filteredRowOrder,
      next.get('rows'),
      (rowId, rowIndex, state) => {
        return state.getIn([rowId, 'item']).toJS();
      },
    );

    next = next.update('rows', (rows) => {
      return rows.map((row, rowId) => {
        const numberAdjusted = derivedFields[rowId].number;
        return row.setIn(['item', 'numberAdjusted'], numberAdjusted);
      });
    });

    if (state) return next;

    this.setStateAndTrigger(next);
  },

  onSaveItemNumber(rowIndex, number) {
    let next = this.getState();

    const row = next.get('rows').toArray()[rowIndex];

    if (!row) return;

    const rowId = row.get('_id');

    next = next.setIn(['rows', rowId, 'item', 'number'], number);
    next = this._recalculateDerivedFields(next);

    this.setStateAndTrigger(next);
  },

  onSaveMultipleDataCellChangesPersistent(
    teamId,
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    changes,
  ) {
    if (showId !== this.getInState('showId')) return;

    let next = this.getState();

    Object.keys(changes).forEach((rowId) => {
      const itemChanges = changes[rowId].ItemTitle;

      if (!itemChanges || !itemChanges.rteState) return;

      const plainTitle = draftJsUtils.getPlainTextFromRawRTEState(
        itemChanges.rteState,
      );

      next = next.update('itemSet', (itemSet) => {
        const rowIndex = itemSet.rows.findIndex((row) => row._id === rowId);
        if (itemSet.rows[rowIndex]) {
          itemSet.rows[rowIndex].item[0].rteTitleState = itemChanges.rteState;
          itemSet.rows[rowIndex].item[0].title = plainTitle;
        }
        return itemSet;
      });

      next = next.updateIn(['rows', rowId, 'item'], (item) => {
        return item
          .set('rteTitleState', itemChanges.rteState)
          .set('title', plainTitle);
      });
    });

    this.setStateAndTrigger(next);
  },

  // eslint-disable-next-line no-unused-vars
  onPublishGlobalElementChangesRowRealTime(params, payload, responsePayload) {
    if (params.showId !== this.getInState('showId')) return;

    const next = this.getState()
      .update('itemSet', (itemSet) => {
        const rowIndex = itemSet.rows.findIndex(
          (row) => row._id === payload._id,
        );

        if (itemSet.rows[rowIndex]) {
          itemSet.rows[rowIndex] = payload;
        }

        return itemSet;
      })
      .updateIn(['rows', payload._id], (row) => {
        return row
          .set('item', fromJS(payload.item[0]))
          .set('titleRow', payload.titleRow);
      });

    this.setStateAndTrigger(next);
  },

  // eslint-disable-next-line no-unused-vars
  onItemRowMutatedRealTime(showId, payload, responsePayload) {
    //If changes are made to a show outside of the global element manager
    //then do nothing. Only update the list for changes inside global element
    //manager
    if (showId !== this.getInState('showId')) return;

    let { rowId, changes } = payload;
    this._updateItemRow(rowId, changes);
  },

  onItemRowMultipleCellsMutatedRealTime(showId, payload) {
    //If changes are made to a show outside of the global element manager
    //then do nothing. Only update the list for changes inside global element
    //manager
    if (this.getInState('showId') !== showId) return;

    const { changes } = payload;

    Object.keys(changes).forEach((rowId) => {
      this._updateItemRow(rowId, changes[rowId]);
    });
  },

  // eslint-disable-next-line no-unused-vars
  onItemRowPresentationUpdatedRealTime(showId, payload, responsePayload) {
    if (showId !== this.getInState('showId')) return;

    let next = this.getState();
    let { rowId, changes } = payload;

    next = next.setIn(['rows', rowId, 'titleRow'], changes.titleRow);

    next = next.update('itemSet', (itemSet) => {
      const rowIndex = itemSet.rows.findIndex((row) => row._id === rowId);

      if (itemSet.rows[rowIndex]) {
        itemSet.rows[rowIndex].titleRow = changes.titleRow;
      }

      return itemSet;
    });

    this.setStateAndTrigger(next);
  },

  onConvertToStandardRowPersistent(
    teamId,
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    rowId,
  ) {
    if (showId !== this.getInState('showId')) return;
    this.setInStateAndTrigger(['rows', rowId, 'titleRow'], false);
  },

  onConvertToTitleRowPersistent(
    teamId,
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    rowId,
  ) {
    if (showId !== this.getInState('showId')) return;
    this.setInStateAndTrigger(['rows', rowId, 'titleRow'], true);
  },

  onUpdateItemRowPersistent(
    teamId,
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    rowId,
    changes,
  ) {
    //If changes are made to a show outside of the global element manager
    //then do nothing. Only update the list for changes inside global element
    //manager
    if (showId !== this.getInState('showId')) return;
    this._updateItemRow(rowId, changes);
  },

  _updateItemRow(rowId, changes) {
    let next = this.getState();
    let updateData = {};

    if (changes.ItemTitle)
      updateData.rteTitleState = changes.ItemTitle.rteState;
    if (changes.rteTitleState) updateData.rteTitleState = changes.rteTitleState;
    if (changes.durationInSeconds)
      updateData.durationInSeconds = changes.durationInSeconds;

    if (updateData.rteTitleState) {
      updateData.title = draftJsUtils.getPlainTextFromRawRTEState(
        updateData.rteTitleState,
      );
    }

    next = next.update('itemSet', (itemSet) => {
      const rowIndex = itemSet.rows.findIndex((row) => row._id === rowId);

      if (itemSet.rows[rowIndex]) {
        itemSet.rows[rowIndex].item[0] = Object.assign(
          {},
          itemSet.rows[rowIndex].item[0],
          updateData,
        );
      }

      return itemSet;
    });

    next = next.updateIn(['rows', rowId, 'item'], (item) => {
      Object.keys(updateData).forEach((key) => {
        item = item.set(key, updateData[key]);
      });

      return item;
    });

    this.setStateAndTrigger(next);
  },

  onDeleteItemRowsPersistent(
    teamId,
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    rowIds,
  ) {
    this._deleteItemRows(rowIds);
  },

  onDeleteItemRowsPersistentCompleted() {
    this._refreshGlobalElementRowAssociations();
  },

  // eslint-disable-next-line no-unused-vars
  onItemRowsDeletedRealTime(showId, { itemSetId, rowIds }) {
    if (this.getInState('showId') !== showId) return;
    this._deleteItemRows(rowIds);
  },

  _deleteItemRows(rowIds) {
    let next = this.getState(),
      itemSet = next.get('itemSet');

    if (!itemSet) return;

    rowIds.forEach((rowId) => {
      let rowIdIndex = itemSet.rowOrder.indexOf(rowId);
      itemSet.rowOrder.splice(rowIdIndex, 1);
    });

    next = next.set('itemSet', itemSet);

    next = this._setPopulatedShowRows(itemSet.rows, itemSet.rowOrder, next);

    this.setStateAndTrigger(next);
  },

  onGlobalElementItemRowsAddedRealTime(showId, payload, responsePayload) {
    /**
     * Prevent row additons being applied to another show from being added to
     * the row list in the current store state. Listening for realtime row
     * additions means we may get add row actions from other shows.
     */
    if (this.getInState('showId') !== showId) return;

    const { itemSetId, afterRowId, rows } = responsePayload;
    this._addItemRows(itemSetId, afterRowId, rows);
  },

  onItemRowsAddedRealTime(showId, payload, responsePayload) {
    /**
     * Prevent row additons being applied to another show from being added to
     * the row list in the current store state. Listening for realtime row
     * additions means we may get add row actions from other shows.
     */
    if (this.getInState('showId') !== showId) return;

    const { itemSetId, afterRowId, rows } = responsePayload;
    this._addItemRows(itemSetId, afterRowId, rows);

    /**
     * When new rows are added via actions or realtime actions we only get the
     * row data. We have to re-request the global element show items because
     * those associations tell us if the new rows that were just added are
     * global element rows or not. The global element show item is the only way
     * to identify whether a row is a global element row or not.
     */
    this._refreshGlobalElementRowAssociations();
  },

  onAddItemRowsPersistentCompleted(result) {
    const { itemSetId, afterRowId, rows } = result;

    /**
     * Prevent rows being added to a different show from showing up in the
     * current show that is populated in this store.
     */
    if (itemSetId !== this.getInState('itemSetId')) return;

    this._addItemRows(itemSetId, afterRowId, rows);
  },

  onAddGlobalElementItemRowsTransient(itemSetId, afterRowId, items) {
    const itemRows = items.map((item) => {
      let row = item.transientRowData;
      row.item = [row.item];
      return row;
    });

    this._addItemRows(itemSetId, afterRowId, itemRows);
  },

  onAddGlobalElementItemRowsPersistentCompleted(result) {
    const { itemSetId, afterRowId, rows } = result;
    this._addItemRows(itemSetId, afterRowId, rows, true);
    this._refreshGlobalElementRowAssociations();
  },

  _addItemRows(itemSetId, afterRowId, rows, removeTransientRows) {
    let next = this.getState();

    const itemSet = next.get('itemSet');

    if (!itemSet) return;

    const indexOfAfterRowId = itemSet.rowOrder.indexOf(afterRowId) + 1;

    const newRowIds = rows.map((r) => r._id).reverse();

    /**
     * Used with transient row additions. When the persisted rows are received
     * we want to replace the transient rows with the new ones.
     */
    if (removeTransientRows) {
      itemSet.rowOrder.splice(indexOfAfterRowId, newRowIds.length);
    }

    newRowIds.forEach((rowId) => {
      itemSet.rowOrder.splice(indexOfAfterRowId, 0, rowId);
    });

    itemSet.rows = itemSet.rows.concat(rows);

    next = next.set('itemSet', itemSet);

    next = this._setPopulatedShowRows(itemSet.rows, itemSet.rowOrder, next);

    this.setStateAndTrigger(next);
  },

  onItemRowMovedFromSourceToDestinationRealTime(
    params,
    payload,
    responsePayload, // eslint-disable-line no-unused-vars
  ) {
    const { itemSetId } = params;

    if (itemSetId !== this.getInState('itemSetId')) return;

    const { source: sourceRowId, target: targetRowIndex } = payload;

    this.onMoveRowFromCurrentPositionToIndex(sourceRowId, targetRowIndex);
  },

  onMoveRowFromCurrentPositionToIndex(sourceRowId, targetRowIndex) {
    let next = this.getState();

    let itemSet = next.get('itemSet');
    let rowOrder = itemSet.rowOrder;

    let sourceRowIndex = rowOrder.indexOf(sourceRowId);

    rowOrder.splice(sourceRowIndex, 1);
    rowOrder.splice(targetRowIndex, 0, sourceRowId);

    itemSet.rowOrder = rowOrder;

    next = next.set('itemSet', itemSet);

    next = this._setPopulatedShowRows(itemSet.rows, itemSet.rowOrder, next);

    this.setStateAndTrigger(next);
  },

  onItemRowsMovedRealTime(showId, { itemSetId }, newRowOrder) {
    if (this.getInState('showId') !== showId) return;
    if (remoteRowOrderDeferred(itemSetId)) return;
    this._updateRowOrder(newRowOrder);
  },

  onItemRowOrderUpdatedRealTime(showId, { itemSetId }, newRowOrder) {
    if (this.getInState('showId') !== showId) return;
    if (remoteRowOrderDeferred(itemSetId)) return;
    this._updateRowOrder(newRowOrder);
  },

  onRowsMovedLocally(
    teamId,
    eventId,
    showId,
    itemSetListId,
    itemSetId,
    rowIds,
    afterRowId,
  ) {
    if (this.getInState('showId') !== showId) return;

    const itemSet = this.getState().get('itemSet');

    //Only apply the delta to the itemSet it belongs to: during populate and
    //element set switches the store can briefly hold no itemSet, or the
    //previous one
    if (!itemSet || itemSet._id !== itemSetId) return;

    if (!applyRowMoveDelta(itemSet.rowOrder, rowIds, afterRowId)) return;

    this._updateRowOrder(itemSet.rowOrder);
  },

  /**
   * Apply the authoritative order the cuesheet re-synced to after its moveRows
   * request settled. We only saw the optimistic delta via onRowsMovedLocally
   * and never receive the HTTP response, so this is how a concurrent remote
   * move (merged into the response) reaches us.
   */
  onRowOrderResynced(showId, itemSetId, rowOrder) {
    if (this.getInState('showId') !== showId) return;

    const itemSet = this.getState().get('itemSet');
    //Only the itemSet this order belongs to — the GEM may be showing another
    if (!itemSet || itemSet._id !== itemSetId) return;

    this._updateRowOrder(rowOrder);
  },

  _updateRowOrder(rowOrder) {
    let next = this.getState();

    let itemSet = next.get('itemSet');

    if (!itemSet) return;

    itemSet.rowOrder = rowOrder;

    next = next.set('itemSet', itemSet);

    next = this._setPopulatedShowRows(itemSet.rows, itemSet.rowOrder, next);

    this.setStateAndTrigger(next);
  },

  /**
   * We track which row was rendered last in the cueesheet to automatically
   * scroll the user to that position when they open the global element insert
   * modal.
   *
   * See: https://github.com/SHOFLO/Issues/issues/4095
   */
  onSetLatestRenderedRowIndex(rowIndex) {
    let next = this.getState();

    const previousRowIndex = next.get('latestRenderedCuesheetRowIndex');

    //1 == row numbers are increasing, 0 = row numbers are decreasing
    const direction = rowIndex > previousRowIndex ? 1 : 0;

    next = next
      .set('latestRenderedCuesheetRowIndex', rowIndex)
      .set('cuesheetScrollDirection', direction);

    this.setStateAndTrigger(next);
  },
});
